/**
 * FormSettingsPanel Component
 *
 * Modal dialog for configuring global form settings including layout,
 * columns, submit button, validation behavior, and styling options.
 */

import { memo, useCallback } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  Select,
  Switch,
  Group,
  Button,
  Text,
  Divider,
  SegmentedControl,
} from '@mantine/core';
import { Icon } from '@/components/ui/icons/Icon';
import {
  useFormBuilderStore,
  selectForm,
  selectFormSettings,
  selectShowFormSettings,
} from '@/stores/formBuilderStore';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import {
  FORM_COLUMN_OPTIONS,
  GAP_OPTIONS,
  BUTTON_POSITION_OPTIONS,
} from './constants';
import type { FormSettings } from './types';
import classes from './FormBuilder.module.css';

/**
 * FormSettingsPanel Component
 */
const FormSettingsPanel = memo(() => {
  const form = useFormBuilderStore(selectForm);
  const settings = useFormBuilderStore(selectFormSettings);
  const showFormSettings = useFormBuilderStore(selectShowFormSettings);
  const { setShowFormSettings, updateFormSettings, updateFormName, updateFormDescription } =
    useFormBuilderStore();

  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const handleClose = useCallback(() => {
    setShowFormSettings(false);
  }, [setShowFormSettings]);

  const handleSettingsChange = useCallback(
    <K extends keyof FormSettings>(key: K, value: FormSettings[K]) => {
      updateFormSettings({ [key]: value });
    },
    [updateFormSettings]
  );

  if (!form) return null;

  return (
    <Modal
      opened={showFormSettings}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <Icon name="settings" size={20} />
          <Text fw={600} style={accessibilityStyles}>Form Settings</Text>
        </Group>
      }
      size="lg"
      padding="lg"
    >
      <Stack gap="lg">
        {/* Form Info */}
        <Stack gap="md">
          <Text fw={600} size="sm" c="dimmed" style={accessibilityStyles}>
            Form Information
          </Text>

          <TextInput
            label="Form Name"
            value={form.name}
            onChange={(e) => updateFormName(e.target.value)}
            placeholder="Enter form name"
            required
          />

          <Textarea
            label="Description"
            value={form.description || ''}
            onChange={(e) => updateFormDescription(e.target.value)}
            placeholder="Optional description of the form"
            rows={2}
          />
        </Stack>

        <Divider />

        {/* Layout Settings */}
        <Stack gap="md">
          <Text fw={600} size="sm" c="dimmed" style={accessibilityStyles}>
            Layout
          </Text>

          <Group grow>
            <Select
              label="Columns"
              value={String(settings.columns)}
              onChange={(value) =>
                handleSettingsChange('columns', Number(value) as 1 | 2 | 3 | 4)
              }
              data={FORM_COLUMN_OPTIONS}
              description="Number of grid columns"
            />

            <Select
              label="Field Gap"
              value={settings.gap}
              onChange={(value) =>
                handleSettingsChange('gap', value as FormSettings['gap'])
              }
              data={GAP_OPTIONS}
              description="Spacing between fields"
            />
          </Group>

          <Stack gap={4}>
            <Text size="sm" fw={500} style={accessibilityStyles}>
              Form Layout
            </Text>
            <SegmentedControl
              value={settings.layout}
              onChange={(value) =>
                handleSettingsChange('layout', value as FormSettings['layout'])
              }
              data={[
                {
                  value: 'vertical',
                  label: (
                    <Group gap={4}>
                      <Icon name="layoutList" size={14} />
                      <span>Vertical</span>
                    </Group>
                  ),
                },
                {
                  value: 'horizontal',
                  label: (
                    <Group gap={4}>
                      <Icon name="layoutGrid" size={14} />
                      <span>Horizontal</span>
                    </Group>
                  ),
                },
                {
                  value: 'inline',
                  label: (
                    <Group gap={4}>
                      <Icon name="alignLeft" size={14} />
                      <span>Inline</span>
                    </Group>
                  ),
                },
              ]}
              fullWidth
            />
          </Stack>

          <Switch
            label="Compact Mode"
            description="Use smaller field sizes"
            checked={settings.compact || false}
            onChange={(e) => handleSettingsChange('compact', e.currentTarget.checked)}
          />
        </Stack>

        <Divider />

        {/* Submit Button Settings */}
        <Stack gap="md">
          <Text fw={600} size="sm" c="dimmed" style={accessibilityStyles}>
            Submit Button
          </Text>

          <Group grow>
            <TextInput
              label="Submit Button Text"
              value={settings.submitButtonText}
              onChange={(e) => handleSettingsChange('submitButtonText', e.target.value)}
              placeholder="Submit"
            />

            <Select
              label="Button Position"
              value={settings.submitButtonPosition}
              onChange={(value) =>
                handleSettingsChange(
                  'submitButtonPosition',
                  value as FormSettings['submitButtonPosition']
                )
              }
              data={BUTTON_POSITION_OPTIONS}
            />
          </Group>

          <Switch
            label="Show Reset Button"
            description="Add a button to reset the form"
            checked={settings.showResetButton}
            onChange={(e) => handleSettingsChange('showResetButton', e.currentTarget.checked)}
          />

          {settings.showResetButton && (
            <TextInput
              label="Reset Button Text"
              value={settings.resetButtonText}
              onChange={(e) => handleSettingsChange('resetButtonText', e.target.value)}
              placeholder="Reset"
            />
          )}
        </Stack>

        <Divider />

        {/* Validation Settings */}
        <Stack gap="md">
          <Text fw={600} size="sm" c="dimmed" style={accessibilityStyles}>
            Validation
          </Text>

          <Switch
            label="Validate on Blur"
            description="Validate field when it loses focus"
            checked={settings.validateOnBlur}
            onChange={(e) => handleSettingsChange('validateOnBlur', e.currentTarget.checked)}
          />

          <Switch
            label="Validate on Change"
            description="Validate field as user types"
            checked={settings.validateOnChange}
            onChange={(e) => handleSettingsChange('validateOnChange', e.currentTarget.checked)}
          />
        </Stack>

        <Divider />

        {/* Styling Settings */}
        <Stack gap="md">
          <Text fw={600} size="sm" c="dimmed" style={accessibilityStyles}>
            Styling
          </Text>

          <Stack gap={4}>
            <Text size="sm" fw={500} style={accessibilityStyles}>
              Button Style
            </Text>
            <SegmentedControl
              value={settings.gradientVariant || 'primary'}
              onChange={(value) =>
                handleSettingsChange(
                  'gradientVariant',
                  value as FormSettings['gradientVariant']
                )
              }
              data={[
                { value: 'primary', label: 'Primary' },
                { value: 'secondary', label: 'Secondary' },
                { value: 'tertiary', label: 'Tertiary' },
              ]}
              fullWidth
            />
          </Stack>
        </Stack>
      </Stack>

      <Group justify="flex-end" mt="xl">
        <Button variant="subtle" onClick={handleClose}>
          Close
        </Button>
      </Group>
    </Modal>
  );
});

FormSettingsPanel.displayName = 'FormSettingsPanel';

export default FormSettingsPanel;
